// import type { TFxWorkerStore } from './types';
import {
  add,
  addDays,
  eachQuarterOfInterval,
  eachYearOfInterval,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isBefore,
  isWithinInterval,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  sub,
} from "date-fns";
import format from "date-fns/format";
import { parseDate } from "devextreme/localization";
import { ensure, Err, Ok } from "effection";
import { padStart } from "lodash";
import { call, sleep } from "starfx";

import { db as workerDb } from "@app/stateCommon/idbSchema";
import { isErr, isOk } from "@app/statefx/helpers";

import { isJSONString } from "../helpers";

import type { TPostBackType } from "@app/types/insider";

import type { Operation, Result } from "effection";

export type TRunnerParameters = {
  payload: string;
  postBack: boolean;
  ama: number;
  amaid: number;
  emitent: string;
  env: string;
  start: Date;
  syncCode: string;
  pbkType: TPostBackType;
  countryCode: string;
};

export function WorkerStorage(store) {
  if (!store) {
    console.warn("store not found 1");
    return;
  }
  const db = workerDb(store);
  return db;
}

export const nYearsInterval = ({
  start,
  end,
  years,
}: {
  start: Date;
  end: Date;
  years: number;
}) => {
  return eachYearOfInterval({
    start: new Date(start),
    end: new Date(end),
  }).reduce((acc, year, index, array) => {
    // Group every n years
    if (index % years === 0) {
      // Start a new group every n years
      const endYear = array[Math.min(index + years - 1, array.length - 1)];
      const startDate = format(startOfYear(year), "yyyy-MM-dd");
      const endDate = format(endOfYear(endYear), "yyyy-MM-dd");
      acc.push({ start: startDate, end: endDate });
    }
    return acc;
  }, []);
};

export function extractRunnerParameters(data, url): TRunnerParameters {
  const { data: payload } = data.options;

  const postBack_ = url.searchParams.get("postBack");
  let postBack;
  if (["null", "undefined", "false"].includes(postBack_)) {
    postBack = false;
  } else {
    postBack = true;
  }

  const amaid = url.searchParams.get("amaid");
  const emitent = url.searchParams.get("emitent");
  const env = url.searchParams.get("env");
  const start_ = url.searchParams.get("start");
  const start = parse(start_, "yyyy-MM-dd", new Date());
  const syncCode = url.searchParams.get("syncCode");
  const pbkType = url.searchParams.get("pbkType");
  const countryCode = url.searchParams.get("countryCode");

  // Return the extracted parameters in an object
  return {
    payload,
    postBack,
    ama: +amaid,
    amaid: +amaid,
    emitent,
    env,
    start,
    syncCode,
    pbkType,
    countryCode,
  };
}

export function invoiceSlices(start) {
  const years = eachYearOfInterval({
    start: new Date(start),
    end: new Date(),
  });

  const range0 = {
    start: format(new Date(start), "yyyy-MM-dd"),
    end: null,
  };

  if (years.length > 20) {
    // forced range on = 18 years
    range0.end = format(
      endOfYear(add(new Date(range0.start), { years: 19 })),
      "yyyy-MM-dd"
    );
  } else {
    if (years.length > 15) {
      // forced range on = 13  years
      range0.end = format(
        endOfYear(add(new Date(range0.start), { years: 14 })),
        "yyyy-MM-dd"
      );
    } else {
      if (years.length > 10) {
        // forced range on = 8 years
        range0.end = format(
          endOfYear(add(new Date(range0.start), { years: 9 })),
          "yyyy-MM-dd"
        );
      } else {
        if (years.length > 5) {
          // forced range on = 3 years
          range0.end = format(
            endOfYear(add(new Date(range0.start), { years: 4 })),
            "yyyy-MM-dd"
          );
        } else {
          if (years.length > 2) {
            range0.end = format(
              endOfYear(new Date(range0.start)),
              "yyyy-MM-dd"
            );
          } else {
            return invoiceSlices_(new Date(range0.start));
          }
        }
      }
    }
  }
  const otherRanges = invoiceSlices_(parseDate(range0.end, "yyyy-MM-dd"));
  const betterRanges = [range0, ...otherRanges];
  return betterRanges;
}

export const invoiceSlices_ = (start) => {
  const years = eachYearOfInterval({
    start: new Date(start),
    end: new Date(),
  });

  const yearSlices = years
    .filter((s) => s < startOfYear(new Date()))
    .map((year) => {
      const start = format(startOfYear(year), "yyyy-MM-dd");
      const end = format(endOfYear(year), "yyyy-MM-dd");
      return { start, end };
    });

  //quarters in current year
  const quarters = eachQuarterOfInterval({
    start: startOfYear(new Date()),
    end: new Date(),
  });

  const quarterSlices = quarters
    .filter((s) => s < startOfQuarter(new Date()))
    .map((quarter) => {
      const start = format(quarter, "yyyy-MM-dd");
      const end = format(endOfQuarter(quarter), "yyyy-MM-dd");
      return { start, end };
    });
  //last startof month of today;
  const lastInteval = {
    start: format(startOfQuarter(new Date()), "yyyy-MM-dd"),
    end: format(endOfMonth(new Date()), "yyyy-MM-dd"),
  };
  const allIntervals = [...yearSlices, ...quarterSlices, lastInteval];
  return allIntervals;
};

export function isEmptyDataset(response) {
  // Check for null or undefined
  if (response == null) {
    return true;
  }

  // Check for an empty array
  if (Array.isArray(response) && response.length === 0) {
    return true;
  }

  if (typeof response === "object" && Object.keys(response).length === 0) {
    return true;
  }
  // Check for the specific JSON format indicating an empty dataset
  if (
    typeof response === "object" &&
    (response?.fieldCount || 0) > 0 &&
    response?.rowCount === 0
  ) {
    return true;
  }
  // If none of the above conditions are true, the dataset is not considered empty
  return false;
}

export function* deleteByPrimaryKey(dbPromise, table, primaryKey) {
  const db = yield* call(dbPromise) as any;
  const tx = db.transaction(table, "readwrite");
  const store = tx.objectStore(table);

  try {
    yield* call(() => store.delete(primaryKey));
    yield* call(() => tx.done);
  } catch (error) {
    console.error(
      "Could not complete the delete operation by primary key:",
      error
    );
  }
}

export function* deleteRange(dbPromise, table, index, lower, upper) {
  const db = yield* call(dbPromise) as any;
  // console.log('db', db)
  const tx = db.transaction(table, "readwrite");
  // console.log('tx', tx)
  const store = tx.store;
  // console.log('store', store)
  const idx = store.index(index);
  // console.log('idx', idx)
  const range = IDBKeyRange.bound(lower, upper);
  // console.log('range', range)
  try {
    let cursor = yield* call(() => idx.openCursor(range));
    let count = 0;

    while (cursor) {
      // Delete the current record
      yield* call(() => cursor.delete());
      count++;
      cursor = yield* call(() => cursor.continue()); // Move to the next record
    }

    yield* call(() => tx.done); // Ensures the transaction completes successfully
    // console.log(
    //   `Deleted ${count} record(s) in the range from ${lower} to ${upper} of ${table}`,
    // );
  } catch (error) {
    console.error("Could not complete the delete operation:", error);
  }
  return;
}

export function parseAsSpvDateString(date) {
  const years = new Date(date).getFullYear();
  const months = new Date(date).getMonth() + 1;
  const day = new Date(date).getDate();
  // left padded with '0' to two digits
  const monthsStr = padStart(months.toString(), 2, "0");
  // left padded with '0' to two digits
  const dayStr = padStart(day.toString(), 2, "0");
  return `${years}${monthsStr}${dayStr}`;
}

// let's do a very good shard builder...

const formattedShards = (s) => {
  return {
    start: format(new Date(s.start), "yyyy-MM-dd"),
    end: format(new Date(s.end), "yyyy-MM-dd"),
  };
};

export function shardBuilder(absoluteStart: Date) {
  // Use current date only once instead of creating multiple instances.
  const currentDate = new Date();

  // Calculate shardMinusOne dates.
  const startShardMinusOne = startOfMonth(sub(currentDate, { days: 60 }));
  const endShardMinusOne = endOfYear(currentDate);
  const shardMinusOne = {
    start: startShardMinusOne,
    end: endShardMinusOne,
  };

  if (!isBefore(absoluteStart, startShardMinusOne)) {
    return [formattedShards(shardMinusOne)];
  }

  // Calculate shardMinusTwo dates using previously calculated startShardMinusOne.
  const startShardMinusTwo = startOfYear(startShardMinusOne);
  const endShardMinusTwo = endOfMonth(sub(startShardMinusOne, { days: 1 }));
  const shardMinusTwo = {
    start: startShardMinusTwo,
    end: endShardMinusTwo,
  };

  if (!isBefore(absoluteStart, startShardMinusTwo)) {
    return [formattedShards(shardMinusTwo), formattedShards(shardMinusOne)];
  }
  // Generate all the remaining shards.
  const shards = nYearsInterval({
    start: absoluteStart,
    end: sub(new Date(startShardMinusTwo), { days: 1 }),
    years: 3,
  });
  const formattedShds = shards.map((s) => formattedShards(s));
  const result = [
    ...formattedShds,
    formattedShards(shardMinusTwo),
    formattedShards(shardMinusOne),
  ].reverse(); /// (!!!) extremely important to reverse the order (!!!)
  return result;
}

interface RequestInitWithBody extends RequestInit {
  body?: BodyInit | null;
}

export function* parseMessageEvent(
  event: MessageEvent,
  setStoreName: (storeName: string) => void,
  getStoreName: () => string
): Operation<
  Result<{
    key: string;
    url: URL;
    req: Request;
    data: any;
    hashPairs?: string[][];
  }>
> {
  yield* ensure(() => {
    console.log("exit parser");
  });
  let data = (event as MessageEvent).data;

  if (isJSONString(data)) {
    data = JSON.parse(data);
  }

  if (data === "hello") {
    if (process.env.NODE_ENV === "development") {
      self.postMessage("pong");
    }

    return Err(new Error("nop"));
  }

  if (!data || data.url === "/sfx/reenter") {
    return Err(new Error("nop"));
  }
  if ((data?.url || "").startsWith("/nop/")) {
    return Err(new Error("nop"));
  }

  if (typeof data === "string" && (data || "").startsWith("idb@")) {
    const userseed = data.split("@")[1];
    const storeName = getStoreName();
    if (storeName === `${userseed}`) {
      return Err(new Error("nop"));
    }

    //  [findme#320]
    setStoreName(`${userseed}`);
    const db = WorkerStorage(storeName);
    if (!db) {
      return Err(new Error("nop"));
    }
    // return Err(new Error('nop'));
    // dont't use "put" it blocks all the saga//
    // the best "spawn" is "run"...

    // THE FIRST, MOST EARLY POINT TO RUN SOMETHING;//
    /* something() */
    //
  }
  if (!data.url) return Err(new Error("nop"));
  const { type: method, options } = data;
  const url = new URL(data.url, "http://0.0.0.0");
  const headers = { "Content-Type": "application/json" };
  let body;
  let hashPairs;
  if (
    (method === "POST" || method === "PUT" || method === "DELETE") &&
    options &&
    options.data
  ) {
    body = JSON.stringify(options.data);
    hashPairs = options.data.hashPairs;
  }
  const reqInit: RequestInitWithBody = { method, headers };

  if (body) {
    reqInit.body = body;
  }
  const req = new Request(url, reqInit);
  const key = data.options?.meta?.key || null;
  return Ok({ key, url, req, data, hashPairs });
}
